import { TypeManagerDecorator } from "../../../main/type.map.service";
import { ChangeDetectionStrategy, Component, computed, input, output, signal, ViewEncapsulation } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import type { IngestionPoint } from "../data.ingestion.service";
import { S25IconComponent } from "../../s25-icon/s25.icon.component";
import type { FieldType } from "../data.ingestion.const";
import {
    getVisiblePaletteGroups,
    INGESTION_FIELD_PALETTE_GROUPS,
    INGESTION_PALETTE_DRAG_DATA_TYPE,
    serializePaletteDragPayload,
    type IngestionPaletteGroupId,
    type IngestionPaletteTile,
} from "./ingestion.field.palette.const";

export type IngestionPaletteTileClickEvent = {
    fieldType: FieldType;
    label: string;
};

@TypeManagerDecorator("s25-ng-ingestion-form-fields-palette")
@Component({
    selector: "s25-ng-ingestion-form-fields-palette",
    standalone: true,
    imports: [CommonModule, FormsModule, S25IconComponent],
    host: {
        class: "ingestion-fields-palette",
    },
    template: `
        <div class="ingestion-fields-palette-inner">
            <p class="sr-only">Click or drag a field tile to add it to the form.</p>

            <label class="ingestion-fields-palette-search-label">
                <span class="sr-only">Search fields</span>
                <input
                    class="c-input ingestion-fields-palette-search"
                    type="search"
                    placeholder="Search fields…"
                    [ngModel]="searchText()"
                    (ngModelChange)="searchText.set($event)"
                />
            </label>

            <div class="ingestion-fields-palette-groups">
                @for (group of visibleGroups(); track group.id) {
                    <section class="ingestion-fields-palette-group">
                        <button
                            type="button"
                            class="ingestion-fields-palette-group-header"
                            [attr.aria-expanded]="isGroupExpanded(group.id)"
                            (click)="toggleGroup(group.id)"
                        >
                            <s25-ng-icon
                                [type]="'caretDown'"
                                class="ingestion-fields-palette-group-caret"
                                [style.transform]="isGroupExpanded(group.id) ? 'rotate(180deg)' : 'rotate(0deg)'"
                                [ariaLabel]="false"
                                [size]="12"
                            />
                            <span class="ingestion-fields-palette-group-name">{{ group.name }}</span>
                        </button>

                        @if (isGroupExpanded(group.id)) {
                            <div class="ingestion-fields-palette-tiles">
                                @for (tile of group.visibleTiles; track tile.fieldType) {
                                    <button
                                        type="button"
                                        class="ingestion-fields-palette-tile"
                                        [class.ingestion-fields-palette-tile--external]="tile.externalLinkStyle"
                                        [class.ingestion-fields-palette-tile--full-width]="tile.fullWidth"
                                        [attr.data-field-type]="tile.fieldType"
                                        [attr.title]="tile.label"
                                        draggable="true"
                                        (dragstart)="onTileDragStart($event, tile)"
                                        (dragend)="onTileDragEnd()"
                                        (click)="onTileClick(tile)"
                                    >
                                        <span class="ingestion-fields-palette-tile-icon">
                                            <s25-ng-icon [type]="tile.icon" [ariaLabel]="false" [size]="16" />
                                        </span>
                                        <span class="ingestion-fields-palette-tile-label">{{ tile.label }}</span>
                                    </button>
                                }
                            </div>
                        }
                    </section>
                } @empty {
                    <p class="ingestion-fields-palette-empty">No fields match your search.</p>
                }
            </div>
        </div>
    `,
    styles: `
        :host {
            display: flex;
            flex-direction: column;
            width: 100%;
            height: 100%;
            min-height: 0;
        }

        .ingestion-fields-palette-inner {
            display: flex;
            flex-direction: column;
            gap: 12px;
            flex: 1;
            min-height: 0;
        }

        .ingestion-fields-palette-search-label {
            display: block;
            flex-shrink: 0;
        }

        .ingestion-fields-palette-search {
            width: 100%;
            height: 2em;
            padding: 0 0.75em;
            box-sizing: border-box;
        }

        .ingestion-fields-palette-groups {
            flex: 1;
            min-height: 0;
            overflow-y: auto;
            display: flex;
            flex-direction: column;
            gap: 12px;
        }

        .ingestion-fields-palette-group-header {
            display: flex;
            align-items: center;
            gap: 6px;
            width: 100%;
            padding: 6px 0;
            border: none;
            background: transparent;
            cursor: pointer;
            text-align: left;
        }

        .ingestion-fields-palette-group-caret {
            flex-shrink: 0;
        }

        .ingestion-fields-palette-group-name {
            flex: 1;
            font-size: 0.78rem;
            font-weight: 600;
            color: var(--color--text-muted);
            text-transform: uppercase;
            letter-spacing: 0.03em;
        }

        .ingestion-fields-palette-tiles {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 6px;
            padding-top: 6px;
        }

        .ingestion-fields-palette-tile {
            display: flex;
            flex-direction: row;
            align-items: center;
            gap: 6px;
            min-height: 32px;
            padding: 6px 8px;
            border: 1px solid var(--color--border);
            border-radius: 4px;
            background: var(--color--page-bg);
            cursor: grab;
            text-align: left;
            min-width: 0;
        }

        .ingestion-fields-palette-tile:active {
            cursor: grabbing;
        }

        .ingestion-fields-palette-tile:hover,
        .ingestion-fields-palette-tile:focus-visible {
            border-color: var(--color--primary, #2563eb);
            outline: none;
        }

        .ingestion-fields-palette-tile--external {
            border-style: dashed;
        }

        .ingestion-fields-palette-tile--full-width {
            grid-column: 1 / -1;
        }

        .ingestion-fields-palette-tile--full-width .ingestion-fields-palette-tile-label {
            white-space: normal;
            overflow: visible;
            text-overflow: unset;
        }

        .ingestion-fields-palette-tile-icon {
            display: inline-flex;
            flex-shrink: 0;
            align-items: center;
            justify-content: center;
            color: var(--color--text-muted);
        }

        .ingestion-fields-palette-tile-icon s25-ng-icon,
        .ingestion-fields-palette-tile-icon svg {
            stroke: currentColor;
            fill: none;
        }

        .ingestion-fields-palette-tile-label {
            flex: 1;
            min-width: 0;
            font-size: 0.75rem;
            line-height: 1.2;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
        }

        .ingestion-fields-palette-empty {
            margin: 0;
            font-size: 0.85rem;
            color: var(--color--text-muted);
        }

        .sr-only {
            position: absolute;
            width: 1px;
            height: 1px;
            padding: 0;
            margin: -1px;
            overflow: hidden;
            clip: rect(0, 0, 0, 0);
            white-space: nowrap;
            border: 0;
        }
    `,
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class IngestionAdminFormFieldsPaletteComponent {
    readonly ingestionPoint = input<IngestionPoint | undefined>(undefined);

    readonly tileClick = output<IngestionPaletteTileClickEvent>();
    readonly paletteDragStart = output<void>();
    readonly paletteDragEnd = output<void>();

    private tileDragOccurred = false;

    readonly searchText = signal("");
    readonly expandedGroupIds = signal<Set<IngestionPaletteGroupId>>(
        new Set(INGESTION_FIELD_PALETTE_GROUPS.filter((group) => group.defaultExpanded).map((group) => group.id)),
    );

    readonly visibleGroups = computed(() =>
        getVisiblePaletteGroups({
            ingestionPoint: this.ingestionPoint(),
            searchText: this.searchText(),
            filterByUse: "all",
        }),
    );

    isGroupExpanded(groupId: IngestionPaletteGroupId): boolean {
        if (this.searchText().trim()) {
            return true;
        }
        return this.expandedGroupIds().has(groupId);
    }

    toggleGroup(groupId: IngestionPaletteGroupId): void {
        if (this.searchText().trim()) {
            return;
        }

        this.expandedGroupIds.update((expanded) => {
            const next = new Set(expanded);
            if (next.has(groupId)) {
                next.delete(groupId);
            } else {
                next.add(groupId);
            }
            return next;
        });
    }

    onTileDragStart(event: DragEvent, tile: IngestionPaletteTile): void {
        this.tileDragOccurred = true;
        const serializedPayload = serializePaletteDragPayload({ fieldType: tile.fieldType, label: tile.label });
        event.dataTransfer?.setData(INGESTION_PALETTE_DRAG_DATA_TYPE, serializedPayload);
        event.dataTransfer?.setData("text/plain", serializedPayload);
        if (event.dataTransfer) {
            event.dataTransfer.effectAllowed = "copy";
        }
        this.paletteDragStart.emit();
    }

    onTileDragEnd(): void {
        this.paletteDragEnd.emit();
        setTimeout(() => {
            this.tileDragOccurred = false;
        });
    }

    onTileClick(tile: IngestionPaletteTile): void {
        if (this.tileDragOccurred) {
            return;
        }
        this.tileClick.emit({ fieldType: tile.fieldType, label: tile.label });
    }
}
